'use client'

import { usePathname } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import { formatImageURL, formatPrice, getEffectivePrice, getStrikethroughPrice, getDisplayDiscounts, isDiscountBetter, isCustomerAuthActive, getProductTags, getYoutubeVideos, getYoutubeVideoId, getVariantDimensions, getSuggestedProducts, formatFileSize, getBuyXGetYTagText, getBuyXGetYFallbackTitle, getLoyaltyProductDiscount } from "@/utils/util"
import { trackViewContent } from "@/utils/tracking"
import { ArrowRight, ChevronRight, FileText, Heart, LoaderCircle, Minus, Plus, Star } from "lucide-react"
import { useCart, useWishlist, useVariantSelection, useProductGallery, useProductQuantity, useCartActions } from "@/lib/commerce/hooks"
import { useScrollVisibility } from "@/hooks/use-scroll-visibility"
import { useSession } from "next-auth/react"
import { navigateUrl } from "@/utils/client-request"
import Review from "./Review"
import ImageCarousel from "@/components/blocks/ImageCarousel"
import ProductDescription from "@/components/blocks/ProductDescription"
import TermsDrawer from "../elements/TermsDrawer"
import SidePanel from "../elements/SidePanel"
import ProductCard from "./ProductCard"
import { OptionPicker } from "./VariantOptions"
import { hasOptions } from "@/lib/commerce/variantOptions"

import Image from "next/image"
import Storepay from "@/assets/storepay.png"
import Pocket from "@/assets/pocket.png"
import Lend from "@/assets/lendmn.png"
import Qpay from "@/assets/qpay.svg"
import TransferIcon from "@/assets/transfer.png"
import InstallmentModal, { type InstallmentProvider } from "../elements/InstallmentModal"

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const primaryButton = `w-full h-12 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`
const secondaryButton = `w-full h-12 border border-foreground text-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`
const sweepLink = 'pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none'

export default function ProductDetail({ productsList, product, vendor, reviews }: { productsList: any[], product: any, vendor: any, reviews: any, wishlist?: any }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  const { loading, loyaltySummary, loyaltyCategoryParents } = useCart()
  const { isInWishlist: isInWishlistFn, toggle: toggleWishlistHook, loading: wishlistLoading } = useWishlist()
  const { addToCartWithRedirect, buyNow, buyNowLoading } = useCartActions(vendor)
  const [isCarouselOpen, setIsCarouselOpen] = useState(false)
  const [termsDrawerOpen, setTermsDrawerOpen] = useState(false)
  const [installmentOpen, setInstallmentOpen] = useState(false)
  const [installmentProvider, setInstallmentProvider] = useState<InstallmentProvider>('storepay')
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedTermsType, setSelectedTermsType] = useState<'delivery' | 'payment' | 'service' | null>(null)
  const { selectedVariantId, setSelectedVariantId, selectedVariant, isVariantAvailable, selectedOptions, selectOption, isSelectionPurchasable } = useVariantSelection(product)

  useEffect(() => {
    if (product?.id) trackViewContent(product)
  }, [product?.id])

  const discounts = getDisplayDiscounts(product, !!session?.user?.id, selectedVariantId ?? undefined)
  const effectivePrice = getEffectivePrice(selectedVariant, discounts, !!session?.user?.id)
  const strikethroughPrice = getStrikethroughPrice(selectedVariant, discounts, !!session?.user?.id)
  const loyaltyDeal = getLoyaltyProductDiscount({ product, variant: selectedVariant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: discounts?.length > 0 && isDiscountBetter(selectedVariant, discounts, !!session?.user?.id) })
  const displayEffective = loyaltyDeal ? Math.max(0, (effectivePrice ?? selectedVariant?.price ?? 0) - loyaltyDeal.unitSaving) : effectivePrice
  const displayStrike = loyaltyDeal ? (selectedVariant?.price ?? 0) : strikethroughPrice

  const images = selectedVariant?.images || []
  const imageUrl = images.length > 0 ? formatImageURL(images[0]) : formatImageURL('placeholder.png')
  const { activeImage, activeImageIndex, selectIndex: handleIndicatorClick, handleTouchStart, handleTouchMove, handleTouchEnd } = useProductGallery(images)
  const canReview = !!session?.user?.id
  const { quantity, setQuantity, handleChange: handleQuantityChange, handleBlur } = useProductQuantity({ variant: selectedVariant, initial: 1 })
  const imageRef = useRef<HTMLDivElement>(null)
  const isNavVisible = useScrollVisibility()

  const averageRating = useMemo(() => reviews?.average || 0, [reviews])
  const publishedReviewsCount = reviews?.totalCount || 0
  const suggestedProducts = useMemo(() => getSuggestedProducts(product, productsList || [], 4), [product, productsList])
  const isInWishlist = isInWishlistFn(product.id, selectedVariantId ?? undefined)

  const videos = getYoutubeVideos(product)
  const tags = getProductTags(product) || []
  const dimensions = getVariantDimensions(selectedVariant)
  const inStock = isSelectionPurchasable && (!product?.isTrackable || (selectedVariant?.stock ?? 0) > 0)
  const unitPrice = displayEffective ?? selectedVariant?.price
  const showReviews = vendor?.canReview && isCustomerAuthActive(vendor)

  const isAllowed = (key: string) => product?.paymentOptions?.[key] !== false
  const showQpay = vendor?.qpayMerchant && vendor?.canQpay !== false && isAllowed('qpay')
  const showTransfer = vendor?.canTransfer && vendor?.canSimple !== false && isAllowed('transfer')
  const showStorepay = vendor?.storepayCredentials && vendor?.canStorepay !== false && isAllowed('storepay')
  const showPocket = vendor?.pocketCredentials && vendor?.canPocket !== false && isAllowed('pocket')
  const showLend = vendor?.lendCredentials && vendor?.canLend !== false && isAllowed('lend')
  const hasPaymentOptions = showQpay || showTransfer || showStorepay || showPocket || showLend

  const variantSales = (product?.variants || []).map((variant: any) => {
    const vDiscounts = getDisplayDiscounts(product, !!session?.user?.id, variant.id)
    const hasPriceSale = variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price
    const hasDiscountSale = vDiscounts?.some((d: any) => d.type === 'productDiscount' && d.method !== 'code') || vDiscounts?.some((d: any) => d.type === 'buyXgetY')
    return !!(hasPriceSale || hasDiscountSale)
  })
  const anyVariantSale = variantSales.some(Boolean)

  const toggleWishlist = (e: any) => {
    if (e) e.preventDefault()
    toggleWishlistHook({ productId: product.id, variantId: selectedVariant?.id })
  }

  const handleAddToCart = async (e: any) => {
    e.preventDefault()
    await addToCartWithRedirect({ productId: product.id, quantity: typeof quantity === 'number' ? quantity : 1, variantId: selectedVariant.id })
  }

  const handleBuyNow = async (e: any) => {
    e.preventDefault()
    await buyNow({ productId: product.id, variantId: selectedVariant.id, quantity: typeof quantity === 'number' ? quantity : 1 })
  }

  const openInstallment = (provider: InstallmentProvider) => {
    setInstallmentProvider(provider)
    setInstallmentOpen(true)
  }

  const handleTermsClick = (type: 'delivery' | 'payment' | 'service') => {
    setSelectedTermsType(type)
    setTermsDrawerOpen(true)
  }

  const openImage = (index: number) => {
    handleIndicatorClick(index)
    setIsCarouselOpen(true)
  }

  const decrement = () => {
    if (quantity === '' || quantity <= 1) setQuantity(1)
    else setQuantity(quantity - 1)
  }

  const increment = () => {
    if (quantity === '') return setQuantity(1)
    if (product?.isTrackable && selectedVariant?.stock) {
      if (quantity < selectedVariant.stock) setQuantity(quantity + 1)
    } else {
      setQuantity(quantity + 1)
    }
  }

  const paymentRow = (logo: any, name: string, desc: string, note?: string) => (
    <>
      <Image src={logo} alt={name} className="size-7 shrink-0 object-contain" />
      <span className="flex-1 min-w-0 text-left">
        <span className="flex items-baseline justify-between gap-3 text-[13px] text-foreground">
          {name}
          {note && <span className="text-[11px] text-muted-foreground whitespace-nowrap">{note}</span>}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{desc}</span>
      </span>
    </>
  )

  return (
    <div className="w-full">
      <ImageCarousel images={images} initialImage={activeImage ?? ''} isOpen={isCarouselOpen} onClose={() => setIsCarouselOpen(false)} productName={product.name} vendor={vendor} />

      <SidePanel open={detailOpen} onClose={() => setDetailOpen(false)} title="Бүтээгдэхүүний дэлгэрэнгүй">
        {product?.description && (
          <div>
            <ProductDescription html={product.description} productName={product.name} vendor={vendor} className="product-description" />
          </div>
        )}
        {dimensions && (
          <p className="mt-6 text-sm"><span className="text-muted-foreground">Хэмжээс:</span> {dimensions}</p>
        )}
        {tags.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2">
            {tags.map((tag: string) => (
              <a key={tag} href={navigateUrl(`/product?tag=${encodeURIComponent(tag)}`, pathname)} className={`text-[13px] ${sweepLink} ${focusRing}`}>{tag}</a>
            ))}
          </div>
        )}
        {videos.length > 0 && (
          <div className="mt-10">
            <h3 className={eyebrow}>Видео</h3>
            <div className="mt-4 flex flex-col gap-2">
              {videos.map((url: string, index: number) => {
                const videoId = getYoutubeVideoId(url)
                if (!videoId) return null
                return (
                  <div key={index} className="relative w-full aspect-16/9 bg-muted">
                    <iframe src={`https://www.youtube.com/embed/${videoId}`} title={`${product.name} video ${index + 1}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 size-full border-0" />
                  </div>
                )
              })}
            </div>
          </div>
        )}
        <ProductTraits product={product} variant={selectedVariant} />
        {hasPaymentOptions && (
          <div className="mt-10">
            <h3 className={eyebrow}>Төлбөрийн боломжууд</h3>
            <div className="mt-4 flex flex-col gap-2">
              {showQpay && <div className="flex items-center gap-4 px-4 py-3 border border-border">{paymentRow(Qpay, 'QPay', 'Бүх банкны аппаар QR код уншуулан төлөх')}</div>}
              {showTransfer && <div className="flex items-center gap-4 px-4 py-3 border border-border">{paymentRow(TransferIcon, 'Дансаар шилжүүлэх', 'Дэлгүүрийн дансанд шилжүүлэг хийж төлөх')}</div>}
              {showStorepay && (
                <button type="button" onClick={() => openInstallment('storepay')} className={`w-full flex items-center gap-4 px-4 py-3 border border-border transition-colors duration-150 hover:border-foreground ${focusRing}`}>
                  {paymentRow(Storepay, 'StorePay', 'Үнийн дүнг 4-6 хуваан төлөх боломжтой', '100,000₮+')}
                  <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                </button>
              )}
              {showPocket && (
                <button type="button" onClick={() => openInstallment('pocket')} className={`w-full flex items-center gap-4 px-4 py-3 border border-border transition-colors duration-150 hover:border-foreground ${focusRing}`}>
                  {paymentRow(Pocket, 'PocketZero', 'Урьдчилгаагүй, шимтгэлгүй 30-90 хоногт 2-6 хувааж төлөх боломжтой', '100,000₮+')}
                  <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                </button>
              )}
              {showLend && (
                <button type="button" onClick={() => openInstallment('lend')} className={`w-full flex items-center gap-4 px-4 py-3 border border-border transition-colors duration-150 hover:border-foreground ${focusRing}`}>
                  {paymentRow(Lend, 'LendMN', 'Урьдчилгаагүй, шимтгэлгүй 30-60 хоногт 1-4 хувааж төлөх боломжтой', '100,000₮+')}
                  <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>
        )}
        <div className="mt-10">
          <h3 className={eyebrow}>Нөхцөл</h3>
          <div className="mt-4 flex flex-col gap-2">
            {([['delivery', 'Хүргэлтийн нөхцөл'], ['payment', 'Төлбөрийн нөхцөл'], ['service', 'Үйлчилгээний нөхцөл']] as const).map(([type, label]) => (
              <button key={type} type="button" onClick={() => handleTermsClick(type)} className={`w-full flex items-center justify-between gap-4 px-4 h-12 border border-border text-[13px] transition-colors duration-150 hover:border-foreground ${focusRing}`}>
                {label}
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
              </button>
            ))}
          </div>
        </div>
        <InstallmentModal open={installmentOpen} onClose={() => setInstallmentOpen(false)} provider={installmentProvider} productPrice={effectivePrice ?? selectedVariant?.price ?? 0} productName={product.name} productImage={activeImage ? activeImage : imageUrl} />
        <TermsDrawer modal isOpen={termsDrawerOpen} onClose={() => setTermsDrawerOpen(false)} type={selectedTermsType} terms={vendor?.terms || {}} vendor={vendor} />
      </SidePanel>

      <div className="lg:grid lg:grid-cols-2 lg:items-start">
        <div className="lg:col-start-1">
          <div className="lg:hidden relative">
            <div ref={imageRef} className="w-full aspect-3/4 relative overflow-hidden bg-muted cursor-zoom-in" onClick={() => openImage(activeImageIndex)} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
              <img src={activeImage ? formatImageURL(activeImage) : imageUrl} alt={product.name} className="w-full h-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1">
                {images.map((_: any, index: number) => (
                  <button key={index} type="button" onClick={() => handleIndicatorClick(index)} aria-label={`${index + 1}-р зураг`} className="h-6 px-0.5 flex items-center">
                    <span className={`block w-5 h-px transition-colors duration-150 ${index === activeImageIndex ? 'bg-foreground' : 'bg-foreground/30'}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="hidden lg:flex flex-col gap-1">
            {(images.length > 0 ? images : ['placeholder.png']).map((image: string, index: number) => (
              <button key={`${image}-${index}`} type="button" onClick={() => images.length > 0 && openImage(index)} aria-label={`${product.name} — ${index + 1}-р зураг томруулах`} className={`block w-full aspect-3/4 bg-muted overflow-hidden cursor-zoom-in ${focusRing}`}>
                <img src={formatImageURL(image)} alt={product.name} loading={index === 0 ? 'eager' : 'lazy'} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

        </div>

        <div className="lg:col-start-2 lg:sticky lg:top-16">
          <div className="lg:min-h-[calc(100svh-4rem)] flex flex-col justify-center lg:items-center px-4 pt-8 pb-12 lg:px-12 lg:py-12">
            <div className="w-full max-w-md xl:max-w-lg">
              <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
                <a href={navigateUrl('/', pathname)} className="hover:text-foreground transition-colors duration-150">Нүүр</a>
                <span aria-hidden>/</span>
                <a href={navigateUrl('/product', pathname)} className="hover:text-foreground transition-colors duration-150">Бүтээгдэхүүн</a>
                {product?.categories?.[0] && (
                  <>
                    <span aria-hidden>/</span>
                    <a href={navigateUrl(`/product?category=${product.categories[0].id}`, pathname)} className="hover:text-foreground transition-colors duration-150">{product.categories[0].name}</a>
                  </>
                )}
              </nav>

              {product.brand && (
                <a href={navigateUrl(`/product?brand=${product.brand.id}`, pathname)} className={`text-[11px] tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground transition-colors duration-150 ${focusRing}`}>
                  {product.brand.name}
                </a>
              )}

              <div className="mt-2 flex items-start justify-between gap-4">
                <h1 className="text-2xl sm:text-3xl tracking-tight text-foreground">{product?.name}</h1>
                {isCustomerAuthActive(vendor) && (
                  <button type="button" onClick={toggleWishlist} aria-label="Хүслийн жагсаалт" aria-pressed={isInWishlist} className={`-mr-2 h-10 w-10 shrink-0 grid place-items-center transition-opacity duration-150 hover:opacity-60 ${focusRing}`}>
                    {wishlistLoading ? <LoaderCircle className="size-5 animate-spin" strokeWidth={1.5} /> : <Heart className={`size-5 ${isInWishlist ? 'fill-current' : ''}`} strokeWidth={1.5} />}
                  </button>
                )}
              </div>

              {vendor?.canReview && (
                <a href="#reviews" className="mt-2 inline-flex items-center gap-1 text-[13px] text-muted-foreground tabular-nums hover:text-foreground transition-colors duration-150">
                  <Star aria-hidden className="size-3.5 fill-current" />
                  {averageRating || 0}
                  {publishedReviewsCount > 0 && <span>({publishedReviewsCount})</span>}
                </a>
              )}

              {selectedVariant?.price > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-lg tabular-nums">
                  {displayEffective !== null ? (
                    <>
                      <span>{displayEffective === 0 ? 'Үнэгүй' : formatPrice(displayEffective)}</span>
                      <span className="text-base line-through text-muted-foreground">{formatPrice(displayStrike)}</span>
                      {selectedVariant?.priceSalePercent && !isDiscountBetter(selectedVariant, discounts, !!session?.user?.id) && !loyaltyDeal?.saleOffset && (
                        <span className="px-2 py-1 border border-foreground text-[11px] leading-none">-{selectedVariant.priceSalePercent}%</span>
                      )}
                      {loyaltyDeal && <span className="px-2 py-1 border border-foreground text-[11px] leading-none">Лояалти -{loyaltyDeal.percent}%</span>}
                    </>
                  ) : (
                    <span>{formatPrice(selectedVariant?.price)}</span>
                  )}
                </div>
              )}
              {loyaltyDeal && (
                <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
                  {loyaltyDeal.tierName} түвшний хөнгөлөлт {loyaltyDeal.percent}%-д хамрагдах бараа тул <span className="text-foreground tabular-nums">{formatPrice(loyaltyDeal.finalPrice)}</span> хөнгөлөлт захиалгад автоматаар тооцогдоно
                </p>
              )}

              <DiscountLedger
                discounts={[
                  ...(isDiscountBetter(selectedVariant, discounts, !!session?.user?.id) ? discounts.filter((d: any) => d.type === 'productDiscount') : []),
                  ...discounts.filter((d: any) => d.type === 'buyXgetY'),
                  ...discounts.filter((d: any) => d.type === 'freeShipping'),
                ]}
                pathname={pathname}
              />

              <form className="mt-8" onSubmit={e => e.preventDefault()}>
                {hasOptions(product) ? (
                  <OptionPicker product={product} selected={selectedOptions} onSelect={selectOption} />
                ) : product?.variants?.length > 1 ? (
                  <fieldset>
                    <legend className="text-[13px]">
                      Төрөл: <span className="text-muted-foreground">{selectedVariant?.name}</span>
                    </legend>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {product.variants.map((variant: any, index: number) => {
                        const isAvailable = isVariantAvailable(variant)
                        const isSelected = selectedVariantId === variant.id
                        const stateClass = isSelected
                          ? isAvailable ? 'border-foreground bg-foreground text-background' : 'border-foreground text-muted-foreground'
                          : isAvailable ? 'border-border hover:border-foreground' : 'border-border text-muted-foreground hover:border-foreground'
                        return (
                          <label key={variant.id} className={`relative inline-flex items-center justify-center min-h-10 min-w-12 px-4 border text-[13px] cursor-pointer transition-colors duration-150 has-focus-visible:outline-1 has-focus-visible:outline-offset-2 has-focus-visible:outline-foreground ${stateClass}`}>
                            <input type="radio" name="variant" value={variant.id} checked={isSelected} onChange={() => setSelectedVariantId(variant.id)} className="sr-only" />
                            <span className={!isAvailable ? 'line-through' : ''}>{variant.name}</span>
                            {variantSales[index] && <span aria-label="Хямдралтай" className={`absolute top-1 right-1 size-1 rounded-full ${isSelected && isAvailable ? 'bg-background' : 'bg-foreground'}`} />}
                          </label>
                        )
                      })}
                    </div>
                    {anyVariantSale && (
                      <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span aria-hidden className="size-1 rounded-full bg-foreground" />
                        Хямдрал, урамшуулалтай төрөл
                      </p>
                    )}
                  </fieldset>
                ) : (
                  product?.variant?.name && product.variant.name !== 'Үндсэн төрөл' && (
                    <div className="text-[13px]">Төрөл: <span className="text-muted-foreground">{product.variant.name}</span></div>
                  )
                )}

                {inStock ? (
                  <div className="mt-6">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[13px]">Тоо ширхэг</span>
                      <div className="inline-flex items-center h-10 border border-border">
                        <button type="button" onClick={decrement} aria-label="Хасах" className={`h-full w-10 grid place-items-center transition-colors duration-150 hover:bg-foreground/5 ${focusRing}`}>
                          <Minus className="size-3.5" strokeWidth={1.5} />
                        </button>
                        <input type="number" inputMode="numeric" value={quantity === '' ? '' : quantity} onChange={e => handleQuantityChange(e.target.value)} onBlur={handleBlur} aria-label="Тоо ширхэг" className="h-full w-12 p-0 text-center text-sm tabular-nums bg-transparent border-0 focus:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                        <button type="button" onClick={increment} aria-label="Нэмэх" className={`h-full w-10 grid place-items-center transition-colors duration-150 hover:bg-foreground/5 ${focusRing}`}>
                          <Plus className="size-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                    {vendor?.showStock && product?.isTrackable && (
                      <div className="mt-2 text-right text-xs text-muted-foreground tabular-nums">Үлдэгдэл {selectedVariant?.stock}</div>
                    )}
                    {vendor?.canPlaceOrder !== false && (
                      <div className="mt-6 flex flex-col gap-2">
                        <button type="button" onClick={handleAddToCart} disabled={loading} className={primaryButton}>
                          {loading ? 'Нэмж байна…' : `Сагсанд хийх · ${formatPrice(unitPrice * (quantity || 1))}`}
                        </button>
                        <button type="button" onClick={handleBuyNow} disabled={loading || buyNowLoading} className={secondaryButton}>
                          {buyNowLoading ? 'Уншиж байна' : 'Худалдаж авах'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-6">
                    {!isSelectionPurchasable ? (
                      <p className="mb-3 text-[13px] text-muted-foreground">Сонгосон хослол байхгүй байна. Өөр сонголт хийнэ үү.</p>
                    ) : vendor?.showStock && product?.isTrackable && <p className="mb-3 text-[13px] text-muted-foreground">Энэ төрлийн бараа дууссан байна.</p>}
                    <button type="button" disabled className={primaryButton}>{isSelectionPurchasable ? 'Дууссан байна' : 'Энэ хослол байхгүй'}</button>
                  </div>
                )}
              </form>

              {vendor?.canPlaceOrder === false && (
                <div className="mt-6 border-l border-foreground pl-4">
                  <p className="text-[13px] text-foreground">Захиалга түр хаагдсан байна</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Манай дэлгүүр нь түр хугацаанд захиалга хүлээн авах боломжгүй болсон тул та дараа дахин оролдоно уу.</p>
                </div>
              )}

              <div className="mt-8">
                <button type="button" onClick={() => setDetailOpen(true)} className={`text-[13px] ${sweepLink} ${focusRing}`}>Дэлгэрэнгүй</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {vendor?.canPlaceOrder !== false && inStock && (
        <div className={`lg:hidden fixed inset-x-0 z-20 px-4 transition-all duration-300 ${isNavVisible ? 'bottom-20' : 'bottom-4'}`}>
          <button type="button" onClick={handleAddToCart} disabled={loading} className={primaryButton}>
            {loading ? 'Нэмж байна…' : 'Сагсанд хийх'}
          </button>
        </div>
      )}

      {showReviews && (
        <section id="reviews" className="w-full px-4 lg:px-6 py-12 lg:py-16 border-t border-border scroll-mt-20">
          <Review product={product} canReview={canReview} vendor={vendor} reviews={reviews} />
        </section>
      )}

      {suggestedProducts.length > 0 && (
        <section className="w-full px-4 lg:px-6 py-12 lg:py-16">
          <h2 className="mb-6 lg:mb-8 text-2xl sm:text-4xl tracking-tight text-foreground">Санал болгох бүтээгдэхүүн</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-1 gap-y-8 lg:gap-y-12">
            {suggestedProducts.map((p: any) => (
              <ProductCard key={p.customId} product={p} vendor={vendor} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function ProductTraits({ product, variant }: { product: any, variant: any }) {
  const file = product?.isDigital ? variant?.digitalFile : null
  const items = [
    product?.isDigital && { key: 'digital', title: 'Дижитал бүтээгдэхүүн', body: 'Худалдан авсны дараа татаж авна' },
    product?.isLimitedEdition && { key: 'limited', title: 'Limited Edition', body: 'Ховор загварын бүтээгдэхүүн' },
    product?.isOrderBased && { key: 'order', title: 'Захиалгаар бэлтгэдэг', body: product?.orderBasedPeriod ? <>Бэлтгэх хугацаа: <span className="text-foreground tabular-nums">{product.orderBasedPeriod}</span></> : 'Тодорхой хугацаанд бэлэн болно' },
  ].filter(Boolean) as { key: string, title: string, body: React.ReactNode }[]

  if (items.length === 0) return null

  return (
    <div className="mt-10">
      <h3 className={eyebrow}>Онцлог</h3>
      <ul className="mt-4 divide-y divide-border border-y border-border">
        {items.map(it => (
          <li key={it.key} className="py-4">
            <div className="text-[13px] text-foreground">{it.title}</div>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{it.body}</p>
            {it.key === 'digital' && file && (
              <div className="mt-3 flex items-center gap-3 px-4 py-3 border border-border text-xs">
                <FileText className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                <span className="flex-1 min-w-0 truncate text-foreground">{file.name}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{formatFileSize(file.size)}</span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

const LEDGER_LABEL: Record<string, string> = { productDiscount: 'Хямдрал', buyXgetY: 'Бэлэг', freeShipping: 'Хүргэлт' }

function DiscountLedger({ discounts, pathname }: { discounts: any[], pathname: string }) {
  const [open, setOpen] = useState(true)
  if (!discounts || discounts.length === 0) return null
  const maxPct = discounts.filter(d => d.type === 'productDiscount' && d.valueType === 'percent').reduce((m, d) => Math.max(m, d.valuePercent || 0), 0)
  const hasBx = discounts.some(d => d.type === 'buyXgetY')
  const hasFs = discounts.some(d => d.type === 'freeShipping')
  const summary = [maxPct > 0 && `-${maxPct}% хүртэл`, hasBx && 'Бэлэгтэй', hasFs && 'Үнэгүй хүргэлт'].filter(Boolean).join(' · ')

  return (
    <div className="mt-6 border-y border-border">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className={`w-full flex items-center justify-between gap-4 py-4 text-left ${focusRing}`}>
        <span className="min-w-0">
          <span className="block text-[13px] text-foreground">Урамшуулал <span className="text-muted-foreground tabular-nums">({discounts.length})</span></span>
          {summary && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{summary}</span>}
        </span>
        {open ? <Minus className="size-3.5 shrink-0" strokeWidth={1.5} /> : <Plus className="size-3.5 shrink-0" strokeWidth={1.5} />}
      </button>
      {open && (
        <ul className="divide-y divide-border border-t border-border">
          {discounts.map((d, i) => (
            <li key={d.id || i} className="py-4"><DiscountLedgerRow d={d} pathname={pathname} /></li>
          ))}
        </ul>
      )}
    </div>
  )
}

function LedgerRow({ type, title, badge, children }: { type: string, title: React.ReactNode, badge?: string | null, children?: React.ReactNode }) {
  return (
    <div>
      <div className={eyebrow}>{LEDGER_LABEL[type]}</div>
      <div className="mt-2 flex items-start justify-between gap-4">
        <span className="min-w-0 text-[13px] text-foreground">{title}</span>
        {badge && <span className="shrink-0 px-2 py-1 border border-foreground text-[11px] leading-none tabular-nums">{badge}</span>}
      </div>
      {children}
    </div>
  )
}

function DiscountLedgerRow({ d, pathname }: { d: any, pathname: string }) {
  if (d.type === 'productDiscount') {
    const badge = d.valueType === 'percent' && d.valuePercent ? `-${d.valuePercent}%` : d.valueType === 'amount' && d.valueAmount ? `-${formatPrice(d.valueAmount)}` : null
    return (
      <LedgerRow type={d.type} title={d.name} badge={badge}>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Энэхүү урамшуулал бараан дээр идэвхтэй байна.</p>
        {d.haveCampaignPage && d.campaignPageHandle && (
          <a href={navigateUrl(`/c/${d.campaignPageHandle}`, pathname)} className={`mt-3 inline-flex items-center gap-1.5 text-xs text-foreground ${sweepLink} ${focusRing}`}>
            Урамшууллын хуудас үзэх
            <ArrowRight className="size-3" strokeWidth={1.5} />
          </a>
        )}
      </LedgerRow>
    )
  }

  if (d.type === 'buyXgetY') {
    const buyX = d.buyXMinQuantity || 1
    const getY = d.getYMaxQuantity || 1
    const reward = d.rewardProducts?.[0] || {}
    const free = reward.valueType === 'free' || !reward.valueType
    const pctVal = reward.valuePercent || d.valuePercent || 0
    const triggers = d.triggerProducts || []
    const isAll = d.buyXMatchMode === 'all' && triggers.length > 1
    return (
      <LedgerRow type={d.type} title={d.name || getBuyXGetYFallbackTitle(d)} badge={getBuyXGetYTagText(d)}>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {isAll ? `Тус бүрээс багадаа ${buyX} ширхэг авбал ` : `${buyX} авбал `}
          {free ? `${getY} бэлэг үнэгүй.` : `${getY} бэлэг ${pctVal}% хямдралтай.`}
        </p>
        {isAll && (
          <ul className="mt-4 grid grid-cols-3 gap-x-1 gap-y-4">
            {triggers.map((tp: any) => {
              const hasDiscount = tp.priceSale && tp.priceSale > 0 && tp.priceSale < tp.price
              return (
                <li key={tp.id}>
                  <a href={navigateUrl(`/product/${tp.customId || tp.id}`, pathname)} className={`group block ${focusRing}`}>
                    <div className="aspect-3/4 overflow-hidden bg-muted">
                      {tp.image && <img src={formatImageURL(tp.image)} alt="" className="size-full object-cover transition-opacity duration-150 group-hover:opacity-85" />}
                    </div>
                    <div className="mt-2 truncate text-xs text-foreground">{tp.name}</div>
                    {tp.price != null && (
                      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-[11px] tabular-nums">
                        <span className="text-foreground">{formatPrice(hasDiscount ? tp.priceSale : tp.price)}</span>
                        {hasDiscount && <span className="line-through text-muted-foreground">{formatPrice(tp.price)}</span>}
                      </div>
                    )}
                  </a>
                </li>
              )
            })}
          </ul>
        )}
      </LedgerRow>
    )
  }

  if (d.type === 'freeShipping') {
    return (
      <LedgerRow type={d.type} title="Үнэгүй хүргэлт">
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground"><span className="text-foreground">{d.name}</span> — энэхүү бараан дээр үнэгүй хүргэлт идэвхтэй.</p>
        {d.isCampaign && d.campaignName && <p className="mt-1 text-xs text-muted-foreground">{d.campaignName}</p>}
      </LedgerRow>
    )
  }

  return null
}
